/**
 * Web Component entry point.
 *
 * Importing this module registers the <legion-opportunities> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * // Register the web component
 * import '@legionhandtech/lht-react-widgets/web-component';
 *
 * // Then use in HTML:
 * // <legion-opportunities base-url="https://your-domain.com" count="5" />
 * ```
 */

import { register as registerOverlayHost } from "./shared/campaign-detail-overlay";

const THEMING_ATTRIBUTES = [
  { attr: "opp-panel-start", param: "oppPanelStart" },
  { attr: "opp-panel-mid", param: "oppPanelMid" },
  { attr: "opp-panel-end", param: "oppPanelEnd" },
  { attr: "opp-panel-color", param: "oppPanelColor" },
  { attr: "opp-panel-shadow", param: "oppPanelShadow" },
  { attr: "opp-panel-blur", param: "oppPanelBlur" },
  { attr: "opp-panel-radius", param: "oppPanelRadius" },
  { attr: "opp-panel-padding", param: "oppPanelPadding" },
  { attr: "opp-panel-max-width", param: "oppPanelMaxWidth" },
  { attr: "opp-card-radius", param: "oppCardRadius" },
  { attr: "opp-card-shadow", param: "oppCardShadow" },
  { attr: "opp-card-border", param: "oppCardBorder" },
  { attr: "opp-card-min-height", param: "oppCardMinHeight" },
  { attr: "opp-title-size", param: "oppTitleSize" },
  { attr: "opp-title-color", param: "oppTitleColor" },
  { attr: "opp-title-weight", param: "oppTitleWeight" },
  { attr: "opp-title-shadow", param: "oppTitleShadow" },
  { attr: "opp-title-font-family", param: "oppTitleFontFamily" },
  { attr: "opp-title-letter-spacing", param: "oppTitleLetterSpacing" },
  { attr: "opp-description-size", param: "oppDescriptionSize" },
  { attr: "opp-description-color", param: "oppDescriptionColor" },
  { attr: "opp-description-shadow", param: "oppDescriptionShadow" },
  { attr: "opp-description-font-family", param: "oppDescriptionFontFamily" },
  { attr: "opp-description-line-height", param: "oppDescriptionLineHeight" },
  { attr: "opp-expires-size", param: "oppExpiresSize" },
  { attr: "opp-expires-color", param: "oppExpiresColor" },
  { attr: "opp-expires-shadow", param: "oppExpiresShadow" },
  { attr: "opp-button-bg", param: "oppButtonBg" },
  { attr: "opp-button-color", param: "oppButtonColor" },
  { attr: "opp-button-radius", param: "oppButtonRadius" },
  { attr: "opp-button-shadow", param: "oppButtonShadow" },
  { attr: "opp-transition-duration", param: "oppTransitionDuration" },
  { attr: "opp-animation-scale", param: "oppAnimationScale" },
] as const;

class LegionOpportunitiesElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private detachOverlay: (() => void) | null = null;

  static get observedAttributes(): string[] {
    return [
      "count",
      "layout",
      "tags",
      "theme",
      "base-url",
      "api-key",
      "auth-token",
      "height",
      ...THEMING_ATTRIBUTES.map((t) => t.attr),
    ];
  }

  connectedCallback(): void {
    this.detachOverlay = registerOverlayHost({
      element: this,
      iframeAccessor: () => this.iframe,
      baseUrl: () => this.baseUrl,
      // Prefer JWT auth-token (forwarded to the detail overlay iframe) over
      // the legacy api-key so both auth mechanisms work correctly.
      tokenAccessor: () =>
        this.getAttribute("auth-token") || this.apiKey || null,
      themeAccessor: () => this.theme,
    });
    this.render();
  }

  disconnectedCallback(): void {
    if (this.detachOverlay) {
      this.detachOverlay();
      this.detachOverlay = null;
    }
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get count(): number {
    const count = parseInt(this.getAttribute("count") || "5", 10);
    return Math.min(Math.max(count, 1), 20);
  }

  private get layout(): string {
    const layout = this.getAttribute("layout") || "horizontal";
    return layout === "vertical" ? "vertical" : "horizontal";
  }

  private get tags(): string {
    return this.getAttribute("tags") || "";
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "auto";
  }

  private get baseUrl(): string {
    return this.getAttribute("base-url") || window.location.origin;
  }

  private get apiKey(): string {
    return this.getAttribute("api-key") || "";
  }

  render(): void {
    this.innerHTML = "";

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/opportunities", this.baseUrl);
    url.searchParams.set("count", this.count.toString());
    url.searchParams.set("layout", this.layout);
    if (this.tags) url.searchParams.set("tags", this.tags);
    if (this.theme) url.searchParams.set("theme", this.theme);

    THEMING_ATTRIBUTES.forEach(({ attr, param }) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(param, val);
      }
    });

    iframe.src = url.toString();
    iframe.width = "100%";
    const isFullHeight =
      this.widgetHeight === "auto" || this.widgetHeight === "100%";
    if (isFullHeight) {
      iframe.style.height = "100%";
      iframe.style.minHeight = this.layout === "vertical" ? "600px" : "320px";
    } else {
      iframe.height = this.widgetHeight;
    }

    iframe.frameBorder = "0";
    iframe.scrolling = "no";
    iframe.title = "Opportunities Widget";
    iframe.style.border = "none";
    iframe.style.borderRadius = "8px";
    iframe.style.overflow = "hidden";

    this.style.display = "block";
    this.style.width = "100%";
    this.style.backgroundColor = "transparent";
    if (isFullHeight) {
      this.style.height = "100%";
      this.style.minHeight = this.layout === "vertical" ? "600px" : "320px";
    } else {
      this.style.height = this.widgetHeight;
    }

    iframe.onload = () => {
      if (iframe.contentWindow && this.apiKey) {
        iframe.contentWindow.postMessage(
          { type: "CONFIG", apiKey: this.apiKey, theme: this.theme },
          this.baseUrl
        );
      }
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: {
            widget: "opportunities",
            count: this.count,
            layout: this.layout,
            tags: this.tags,
          },
        })
      );
    };

    iframe.onerror = () => {
      this.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load widget</div>`;
    };

    this.appendChild(iframe);
    this.iframe = iframe;
  }
}

// Only register if not already registered
if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-opportunities")
) {
  customElements.define("legion-opportunities", LegionOpportunitiesElement);
}

export { LegionOpportunitiesElement };
